/**
 * **端口悬停浮层的数据侧** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 鼠标停在端口上时那个跟着走的浮层：端口名 / 类型 / **当前值**。
 * 用浮层而不是原生 `title` —— 原生 tooltip 要等一秒、跟着系统主题、
 * 还没法做富文本，调试模板时太慢。
 *
 * 「当前值」有三种来源，按优先级：
 *   1. 输出端口 → 本节点求值出来的值
 *   2. 输入端口 → 有连线就取上游（上游没值就说「上游无值」）
 *   3. 没连线   → 内联控件的值（标注「（内联）」）
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 *
 * ⚠️ **`onCanvasPointerOver` / `onCanvasPointerMove` 刻意留在宿主**：
 * 后者是「画布移动」的总入口（记 `lastCanvasPoint`、转发触摸长按），
 * 不只是浮层的事。这里只提供数据和值文本。
 */
import { ref } from 'vue'
import { formatValue } from '@/node/evaluate'
import type { EvalResult } from '@/node/evaluate'
import { sameHandle } from '@/graph/connectionRules'
import type { GraphNodeModel } from '@/types/node'
import type { EdgeView } from './useSubgraphLevels'

export interface SocketTip {
  name: string
  type: string
  value: string
  x: number
  y: number
}

export interface SocketTipCtx {
  /** 求值结果（声明在宿主更靠后的位置，所以用闭包） */
  evalResult: () => EvalResult
  /** 边视图（同上） */
  edgeViews: () => EdgeView[]
  /** 按 id 取模型（同上） */
  modelOf: (id: string) => GraphNodeModel | undefined
}

export function useSocketTip(ctx: SocketTipCtx) {
  const { evalResult, edgeViews, modelOf } = ctx

const socketTip = ref<SocketTip | null>(null)

/** 端口**当前值**的文本。三种来源：本节点输出 / 上游 / 内联控件 */
function socketValueText(nodeId: string, handleId: string): string {
  // 输出端口：本节点求值出来的值
  const outputs = evalResult().outputs.get(nodeId)
  if (outputs?.has(handleId)) return formatValue(outputs.get(handleId))

  // 输入端口：有连线就取上游
  const edge = edgeViews().find(
    (e) => e.target === nodeId && sameHandle(e.targetHandle, handleId),
  )
  if (edge) {
    const upstream = evalResult().outputs
      .get(edge.source)
      ?.get(edge.sourceHandle ?? '')
    return upstream === undefined ? '上游无值' : formatValue(upstream)
  }

  // 没连线 → 内联控件值
  const row = modelOf(nodeId)?.rows.find(
    (r) => r.kind === 'input' && r.socket.id === handleId,
  )
  if (row && row.kind === 'input' && row.widget) {
    return `${formatValue(row.widget.value)}（内联）`
  }
  return '未连接'
}

  return { socketTip, socketValueText }
}
